
import AuthLayout from "@/components/AuthLayout";
import LoginForm from "@/components/auth/LoginForm";
import { RegisterForm } from "@/components/auth/RegisterForm";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";

export default function LoginPage() {
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { resetPassword } = useAuth();
  const { toast } = useToast();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail) {
      toast({
        title: "E-mailadres is verplicht",
        description: "Voer je e-mailadres in om je wachtwoord te resetten.",
        variant: "destructive",
      });
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      toast({
        title: "Ongeldig e-mailadres",
        description: "Voer een geldig e-mailadres in.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error } = await resetPassword(resetEmail);
      
      if (error) {
        toast({
          title: "Fout bij wachtwoord reset",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Reset link verzonden",
          description: "We hebben een link gestuurd om je wachtwoord te resetten. Check je inbox.",
        });
        // Ga terug naar login scherm na succesvolle aanvraag
        setShowForgotPassword(false);
      }
    } catch (error: any) {
      toast({
        title: "Fout bij wachtwoord reset",
        description: "Er is iets misgegaan. Probeer het later opnieuw.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showForgotPassword) {
    return (
      <AuthLayout>
        <Card className="w-full">
          <CardHeader className="space-y-1">
            <div className="flex items-center mb-2">
              <Button 
                variant="ghost" 
                className="p-0 mr-2" 
                onClick={() => setShowForgotPassword(false)}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Terug
              </Button>
            </div>
            <CardTitle className="text-2xl">Wachtwoord vergeten</CardTitle>
            <CardDescription>
              Voer je e-mailadres in om een wachtwoord reset link te ontvangen
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="E-mailadres"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Reset link verzenden..." : "Reset link verzenden"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Tabs defaultValue="login" className="mt-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="login">Inloggen</TabsTrigger>
          <TabsTrigger value="register">Registreren</TabsTrigger>
        </TabsList>
        <TabsContent value="login" className="space-y-4 py-4">
          <LoginForm onForgotPassword={() => setShowForgotPassword(true)} />
        </TabsContent>
        <TabsContent value="register" className="space-y-4 py-4">
          <RegisterForm />
        </TabsContent>
      </Tabs>
    </AuthLayout>
  );
}
